import { IcTrackingCorner } from '@assets/icons/cameras';
import { AbsoluteCenter, Box, For, Icon, Text } from '@chakra-ui/react';
import { formatUnit, OtherUnits } from '@utils/unit.utils';

import { TrackerLocation } from '@/types/sensor/sensor.types';
import { TrackingStatusEnum } from '@/types/sensor/sensors';

import { ControlState } from './CameraControl';

type Props = {
  componentDimension: DOMRect | null;
  effectiveVideoDimension: DOMRect | null;
  dimension16_9: DOMRect | null;
  replayMode: boolean;
  selectedCameraKey: string | null;
  controlState: ControlState;
  detectedObjects: TrackerLocation[];
  trackingAction: TrackingStatusEnum.ACTIVE | TrackingStatusEnum.SCANNING;
  holopticsIrLetterboxSize: {
    width: number;
    height: number;
  };
};

const MIN_TRACKER_OBJECT_SIZE = 80;

export default function CameraTrackingObjects({
  componentDimension,
  effectiveVideoDimension,
  dimension16_9,
  controlState,
  detectedObjects,
  trackingAction,
  holopticsIrLetterboxSize,
}: Readonly<Props>) {
  const letterboxSize = {
    width: ((componentDimension?.width ?? 0) - (effectiveVideoDimension?.width ?? 0)) / 2,
    height: ((componentDimension?.height ?? 0) - (effectiveVideoDimension?.height ?? 0)) / 2,
  };

  function getTrackingCrosshairTransform(detection: TrackerLocation) {
    // box contains x,y position of the target relative to native flux (native resolution with strips for holoptics IR)
    if (
      componentDimension === null ||
      dimension16_9 === null ||
      effectiveVideoDimension === null ||
      controlState.displayedFlux === null
    ) {
      return '';
    }

    const screenX =
      (Math.max(MIN_TRACKER_OBJECT_SIZE, detection.x) * effectiveVideoDimension.width) /
      controlState.displayedFlux.resolutionX;
    const screenY =
      (Math.max(MIN_TRACKER_OBJECT_SIZE, detection.y) * effectiveVideoDimension.height) /
      controlState.displayedFlux.resolutionY;

    const totalTranslate = {
      x: screenX + letterboxSize.width - holopticsIrLetterboxSize.width - componentDimension.width / 2,
      y: screenY + letterboxSize.height - holopticsIrLetterboxSize.height - componentDimension.height / 2,
    };

    return `translate(${totalTranslate.x}px,${totalTranslate.y}px)`;
  }

  function DetectionComponent({
    detection,
    isTracking,
  }: Readonly<{ detection: TrackerLocation; isTracking: boolean }>) {
    if (detection.id === undefined || detection.label === undefined || detection.score === undefined) {
      return null;
    }

    const cornerBorderColor = isTracking ? 'neutral.white' : 'neutral.black';

    return (
      <AbsoluteCenter pointerEvents="none">
        <Box
          style={{
            transform: getTrackingCrosshairTransform(detection),
            width: Math.max(MIN_TRACKER_OBJECT_SIZE, detection.width),
            height: Math.max(MIN_TRACKER_OBJECT_SIZE, detection.height),
          }}
        >
          <Icon as={IcTrackingCorner} color={cornerBorderColor} boxSize="28px" position="absolute" top={0} right={0} />
          <Icon
            as={IcTrackingCorner}
            color={cornerBorderColor}
            boxSize="28px"
            position="absolute"
            top={0}
            left={0}
            transform="rotate(-90deg)"
          />
          <Icon
            as={IcTrackingCorner}
            color={cornerBorderColor}
            boxSize="28px"
            position="absolute"
            bottom={0}
            right={0}
            transform="rotate(90deg)"
          />
          <Icon
            as={IcTrackingCorner}
            color={cornerBorderColor}
            boxSize="28px"
            position="absolute"
            bottom={0}
            left={0}
            transform="rotate(180deg)"
          />
          {!isTracking && (
            <>
              <Box
                position="absolute"
                paddingX={1}
                paddingY={0.125}
                top={0}
                left={0}
                transform="translateY(calc(-100% - 4px))"
                backgroundColor="red.500"
              >
                <Text fontSize="32px" lineHeight="32px" fontWeight="medium">
                  {detection.id}
                </Text>
              </Box>
              <Box
                position="absolute"
                paddingX={0.25}
                top={0}
                right={0}
                transform="translateY(calc(-100% - 4px))"
                backgroundColor="red.800"
              >
                <Text fontSize="22px" lineHeight="22px">
                  {formatUnit(OtherUnits.PERCENT, detection.score)}
                </Text>
              </Box>
              <Box
                position="absolute"
                paddingX={0.25}
                bottom={0}
                left="50%"
                transform="translate(-50%, calc(100% + 4px))"
                backgroundColor="neutral.black"
                width="max-content"
              >
                <Text fontSize="24px" lineHeight="24px" lineClamp={1} wordBreak="none">
                  {detection.label}
                </Text>
              </Box>
            </>
          )}
        </Box>
      </AbsoluteCenter>
    );
  }

  return (
    <For each={detectedObjects}>
      {(detection: TrackerLocation) => (
        <DetectionComponent
          key={detection.id}
          detection={detection}
          isTracking={trackingAction === TrackingStatusEnum.ACTIVE}
        />
      )}
    </For>
  );
}
